'use client'

import React, { useRef, useEffect, useState, useCallback, ReactNode, useMemo } from 'react'
import { Shard } from './Shard'
import { cn } from '@/utilities/ui'
import { debounce } from 'lodash'
import { OverflowNegativeMargin } from './OverflowNegativeMargin'
import { getPseudoRandomShardList } from './Pixelator/shards'

const randomUpdateInterval = 3000

// Tailwind breakpoints
const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

type BreakpointKey = 'sm' | 'md' | 'lg' | 'xl' | '2xl'
type ResponsiveWidth = Partial<Record<BreakpointKey | 'default', string>>

interface ElementPosition {
  row: number
  col: number
  width: ResponsiveWidth
  colSpan?: number // Optional fixed column span (alternative to percentage width)
}

interface PositionedElement {
  element: ReactNode
  position: ElementPosition
  className?: string
}

interface PositionedShardGridProps {
  className?: string
  minGapSize?: number
  elements?: PositionedElement[]
  enableRandomUpdates?: boolean
}

const cellSize = 30
const negativeMargin = 50

export function ShardGridWithElements({
  className,
  minGapSize = 8,
  elements = [],
  enableRandomUpdates = true,
}: PositionedShardGridProps) {
  const gridRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [gap, setGap] = useState(16) // Default gap in pixels
  const [dimensions, setDimensions] = useState({ cols: 6, rows: 2, count: 12 })
  const [elementSpans, setElementSpans] = useState<Record<number, number>>({})
  const [isLayoutCalculated, setIsLayoutCalculated] = useState(false)
  const [animatingShardIndex, setAnimatingShardIndex] = useState<number | null>(null)
  const [isNewShard, setIsNewShard] = useState(false)

  // Track which positions are occupied by custom elements
  const getOccupiedPositions = useCallback(() => {
    const occupied: Set<string> = new Set()

    elements.forEach((el, index) => {
      const span = elementSpans[index] || 1
      const { row, col } = el.position

      // Mark all cells covered by this element as occupied
      for (let c = 0; c < span; c++) {
        if (col + c <= dimensions.cols) {
          occupied.add(`${row}-${col + c}`)
        }
      }
    })

    return occupied
  }, [elements, elementSpans, dimensions.cols])

  const occupiedPositions = getOccupiedPositions()

  // Memoize shard list based on dimensions
  const shardDataList = useMemo(() => {
    return Array.from({ length: dimensions.rows * dimensions.cols })
      .map((_, index) => {
        const row = Math.floor(index / dimensions.cols) + 1
        const col = (index % dimensions.cols) + 1
        const position = `${row}-${col}`

        // Don't render shards where custom elements will be placed
        if (occupiedPositions.has(position)) {
          return null
        }

        return {
          key: `shard-${index}`,
          row,
          col,
        }
      })
      .filter((item): item is { key: string; row: number; col: number } => item !== null)
  }, [dimensions.rows, dimensions.cols, occupiedPositions])

  const [shardList, setShardList] = useState(() => getPseudoRandomShardList(dimensions.count))
  // Keep shard list in sync with dimensions
  useEffect(() => {
    setShardList(getPseudoRandomShardList(dimensions.count))
  }, [dimensions.count])

  // Animate and update a specific shard by index
  const animateAndUpdateShard = useCallback((index: number) => {
    setShardList((currentList) => {
      const newList = [...currentList]
      const newShards = getPseudoRandomShardList(1)
      newList[index] = newShards[0]!
      setAnimatingShardIndex(index)
      setIsNewShard(false)
      setTimeout(() => {
        setIsNewShard(true)
        setTimeout(() => {
          setAnimatingShardIndex(null)
        }, 100)
      }, 100)
      return newList
    })
  }, [])

  // Update a random shard with a new style
  const updateRandomShard = useCallback(() => {
    setShardList((currentList) => {
      const newList = [...currentList]
      const randomIndex = Math.floor(Math.random() * currentList.length)
      const newShards = getPseudoRandomShardList(1)
      // We know getPseudoRandomShardList always returns a non-empty array
      newList[randomIndex] = newShards[0]!

      // Start animation
      setAnimatingShardIndex(randomIndex)
      setIsNewShard(false)

      // After 100ms, show the new shard
      setTimeout(() => {
        setIsNewShard(true)
        // After another 100ms, end the animation
        setTimeout(() => {
          setAnimatingShardIndex(null)
        }, 100)
      }, 100)

      return newList
    })
  }, [])

  // Effect for random shard updates
  useEffect(() => {
    if (!enableRandomUpdates) return

    const interval = setInterval(updateRandomShard, randomUpdateInterval)
    return () => clearInterval(interval)
  }, [enableRandomUpdates, updateRandomShard])

  // Calculate the optimal layout based on container dimensions
  const calculateLayout = useCallback(() => {
    if (!containerRef.current) return

    const container = containerRef.current
    const containerWidth = container.offsetWidth
    const containerHeight = container.offsetHeight || 200 // Default height

    // Calculate optimal number of columns based on container width
    // For small screens, ensure we still show at least 4 columns
    const minColumns = 4
    const maxCols = Math.floor((containerWidth + minGapSize) / (cellSize + minGapSize))
    const cols = Math.max(minColumns, maxCols)

    // Calculate optimal gap - ensure it's not too large on small screens
    const totalCellWidth = cols * cellSize
    const availableSpace = containerWidth - totalCellWidth
    const calculatedGap = cols > 1 ? availableSpace / (cols - 1) : minGapSize
    const finalGap = Math.max(minGapSize, Math.min(calculatedGap, 24)) // Cap the max gap at 24px

    // Calculate rows - ensure we have at least 3 rows
    const minRows = 3
    const rowsPossible = Math.floor((containerHeight + finalGap) / (cellSize + finalGap))
    const rows = Math.max(minRows, rowsPossible)

    // Determine current breakpoint based on container width
    let newBreakpoint: BreakpointKey | 'default' = 'default'
    if (containerWidth >= BREAKPOINTS['2xl']) {
      newBreakpoint = '2xl'
    } else if (containerWidth >= BREAKPOINTS.xl) {
      newBreakpoint = 'xl'
    } else if (containerWidth >= BREAKPOINTS.lg) {
      newBreakpoint = 'lg'
    } else if (containerWidth >= BREAKPOINTS.md) {
      newBreakpoint = 'md'
    } else if (containerWidth >= BREAKPOINTS.sm) {
      newBreakpoint = 'sm'
    }

    // Calculate column spans for percentage-based elements
    const spans: Record<number, number> = {}
    elements.forEach((el, index) => {
      if (el.position.colSpan) {
        // Use fixed column span if provided
        spans[index] = Math.min(el.position.colSpan, cols) // Don't exceed total cols
      } else if (el.position.width) {
        // Get the width for current breakpoint with fallback chain
        let widthValue = '100%'

        if (typeof el.position.width[newBreakpoint] === 'string') {
          widthValue = el.position.width[newBreakpoint] as string
        } else if (typeof el.position.width.default === 'string') {
          widthValue = el.position.width.default
        }

        // Parse percentage and calculate columns
        const percentageMatch = widthValue ? widthValue.match(/^(\d+)%$/) : null
        const percentage =
          percentageMatch && percentageMatch[1] ? parseInt(percentageMatch[1], 10) / 100 : 1 // Default to 100% if format is invalid

        const calculatedSpan = Math.max(1, Math.round(percentage * cols))
        spans[index] = calculatedSpan
      } else {
        // Default to 1 column if no width information is provided
        spans[index] = 1
      }
    })
    setElementSpans(spans)

    // Update state
    setGap(finalGap)
    setDimensions({
      cols,
      rows,
      count: cols * rows,
    })
    setIsLayoutCalculated(true)
  }, [minGapSize, elements])

  // Create debounced version of the layout calculation with lodash
  const debouncedCalculateLayout = useCallback(
    debounce(calculateLayout, 200), // Debounce to 200ms
    [calculateLayout],
  )

  useEffect(() => {
    // Initial calculation (not debounced)
    calculateLayout()

    // Add debounced resize listener
    window.addEventListener('resize', debouncedCalculateLayout)

    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedCalculateLayout)
      // Cancel any pending debounced calls
      debouncedCalculateLayout.cancel?.()
    }
  }, [calculateLayout, debouncedCalculateLayout])

  return (
    <OverflowNegativeMargin margin={negativeMargin}>
      <div
        ref={containerRef}
        className={cn('w-full h-full flex items-center justify-center', className)}
        suppressHydrationWarning
      >
        <div
          ref={gridRef}
          className={cn('grid w-full relative', !isLayoutCalculated && 'invisible')}
          style={{
            gridTemplateColumns: `repeat(${dimensions.cols}, minmax(0, 1fr))`,
            gridTemplateRows: `repeat(${dimensions.rows}, ${cellSize}px)`,
            gap: `${gap}px`,
            justifyContent: 'space-between', // Distribute columns evenly
          }}
        >
          {/* Render background shards */}
          {shardDataList.map(({ key, row, col }, index) => (
            <div
              key={key}
              style={{
                gridRow: row,
                gridColumn: col,
                justifySelf: 'center',
              }}
              className={cn(
                'transition-all duration-200 cursor-pointer',
                animatingShardIndex === index && !isNewShard && 'scale-0 opacity-0',
                animatingShardIndex === index && isNewShard && 'scale-150',
              )}
              onClick={() => {
                if (animatingShardIndex === index) return
                animateAndUpdateShard(index)
              }}
            >
              <Shard shard={shardList[index]} />
            </div>
          ))}

          {/* Render custom elements */}
          {elements.map((el, index) => {
            const { row, col } = el.position
            const span = elementSpans[index] || 1

            return (
              <div
                key={`element-${index}`}
                className={cn('flex items-center', el.className)}
                style={{
                  gridRow: row,
                  gridColumn: `${col} / span ${span}`,
                  minHeight: `${cellSize}px`,
                }}
              >
                {el.element}
              </div>
            )
          })}
        </div>
      </div>
    </OverflowNegativeMargin>
  )
}
